import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Empty, Segmented, Spin, Table, Tag, Typography } from 'antd';
import { CalendarOutlined, LoadingOutlined } from '@ant-design/icons';
import apiClient from '../../../services/apiClient';
import { currency, fmt } from './portfolioHoldingPanels';

const { Text } = Typography;
const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const VIEW_MODES = [
  { label: 'Monthly', value: 'monthly' },
  { label: 'Quarterly', value: 'quarterly' },
  { label: 'Half Annually', value: 'half-annually' },
  { label: 'Annually', value: 'annually' },
  { label: 'Other', value: 'other' },
];

function resolvePaymentFrequency(description) {
  const text = String(description || '').toLowerCase();
  if (!text.trim()) return 'other';
  if (/\bmonthly\b/.test(text) || /\bevery\s+month\b/.test(text) || /\bonce\s+a\s+month\b/.test(text)) {
    return 'monthly';
  }
  if (/\bquarterly\b/.test(text) || /\bevery\s+quarter\b/.test(text) || /\bonce\s+a\s+quarter\b/.test(text)) {
    return 'quarterly';
  }
  if (
    /\bsemi[-\s]?annual/.test(text)
    || /\bhalf[-\s]?(yearly|year|annually|annual)\b/.test(text)
    || /\bbi[-\s]?annual/.test(text)
    || /\btwice\s+(a\s+year|yearly|annually)\b/.test(text)
    || /\btwo\s+times\s+a\s+year\b/.test(text)
    || /\b2\s+times\s+a\s+year\b/.test(text)
  ) {
    return 'half-annually';
  }
  if (
    /\byearly\b/.test(text)
    || /\bannually\b/.test(text)
    || /\bannual\b/.test(text)
    || /\bevery\s+year\b/.test(text)
    || /\bonce\s+a\s+year\b/.test(text)
  ) {
    return 'annually';
  }
  return 'other';
}

function parseDate(value) {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;

  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const d = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const dmy = raw.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (dmy) {
    const d = new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1]));
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatScheduleType(value) {
  if (!value) return '—';
  return String(value).replace(/_/g, ' ');
}

function holdingLookup(holdings) {
  const map = {};
  (holdings || []).forEach((h) => {
    if (h?.isin) map[h.isin] = h;
  });
  return map;
}

function scaleFlow(row, holding) {
  const qty = Number(holding?.quantity) || 1;
  const amount = Number(row.paymentAmount) || 0;
  return {
    ...row,
    isin: row.isin || holding?.isin,
    issuer: holding?.issuer || row.issuer || '',
    quantity: qty,
    scaledAmount: amount * qty,
    paymentDateParsed: parseDate(row.paymentDate),
    frequencyDescription: row.frequencyDescription || holding?.interestPaymentFrequency || '',
    paymentFrequency: resolvePaymentFrequency(
      row.frequencyDescription || holding?.interestPaymentFrequency || ''
    ),
  };
}

async function fetchCalculatedFlows(holding) {
  const payload = { isin: holding.isin };
  if (holding.createdAt) payload.investmentTimestamp = String(holding.createdAt);
  const res = await apiClient.post(`${BASE_URL}/bond-cash-flow/calculate`, payload);
  const dto = res.data?.data;
  const rows = dto?.cashFlows || [];
  const frequencyDescription = dto?.frequencyDescription || '';
  return rows.map((cf) => scaleFlow({
    isin: holding.isin,
    scheduleType: cf.cashFlows || 'CASH_FLOW',
    paymentDate: cf.paymentDate || cf.couponDate,
    paymentAmount: cf.amount,
    paymentType: cf.cashFlows || '',
    description: '',
    frequencyDescription,
  }, holding));
}

async function fetchScheduleFlows(holdings) {
  const isins = holdings.map((h) => h.isin).filter(Boolean);
  if (!isins.length) return [];

  const res = await apiClient.post(`${BASE_URL}/bond-cash-flow/schedules`, { isins });
  const rows = res.data?.data || [];
  const byIsin = holdingLookup(holdings);

  return rows.map((row) => scaleFlow(row, byIsin[row.isin]));
}

function scheduleTypeColor(value) {
  const v = String(value || '').toUpperCase();
  if (v.includes('REDEMPTION')) return 'gold';
  if (v.includes('INTEREST')) return 'green';
  return 'blue';
}

export default function PortfolioCashFlowTab({ holdings }) {
  const [viewMode, setViewMode] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const [allFlows, setAllFlows] = useState([]);
  const [error, setError] = useState(null);
  const [expandedKeys, setExpandedKeys] = useState([]);

  const loadAll = useCallback(async () => {
    if (!holdings?.length) {
      setAllFlows([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      let merged = [];
      try {
        merged = await fetchScheduleFlows(holdings);
      } catch (scheduleErr) {
        console.error('Failed to load india_bond_cash_flow_schedule rows', scheduleErr);
      }

      const covered = new Set(merged.map((row) => row.isin).filter(Boolean));
      const missing = holdings.filter((h) => h.isin && !covered.has(h.isin));
      if (missing.length) {
        const fallback = await Promise.allSettled(missing.map(fetchCalculatedFlows));
        merged = merged.concat(
          fallback.filter((r) => r.status === 'fulfilled').flatMap((r) => r.value)
        );
        if (fallback.some((r) => r.status === 'rejected')) {
          setError('Some cash flow schedules could not be loaded.');
        }
      }

      setAllFlows(merged);
    } catch (e) {
      console.error('Failed to load portfolio cash flows', e);
      setError('Failed to load cash flow data.');
      setAllFlows([]);
    } finally {
      setLoading(false);
    }
  }, [holdings]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const visibleFlows = useMemo(
    () => allFlows.filter((cf) => (cf.paymentFrequency || 'other') === viewMode),
    [allFlows, viewMode]
  );

  const upcomingTotal = useMemo(() => {
    const now = new Date();
    return visibleFlows
      .filter((cf) => cf.paymentDateParsed && cf.paymentDateParsed >= now)
      .reduce((sum, cf) => sum + cf.scaledAmount, 0);
  }, [visibleFlows]);

  const tableData = useMemo(() => {
    const buckets = {};
    visibleFlows.forEach((cf) => {
      const key = cf.isin || 'Unknown';
      if (!buckets[key]) {
        buckets[key] = {
          key,
          isin: key,
          issuer: cf.issuer || '',
          amount: 0,
          events: 0,
          flows: [],
        };
      }
      buckets[key].amount += cf.scaledAmount;
      buckets[key].events += 1;
      if (!buckets[key].issuer && cf.issuer) buckets[key].issuer = cf.issuer;
      buckets[key].flows.push({
        ...cf,
        key: `${cf.isin}-${cf.scheduleType}-${cf.paymentDate}-${buckets[key].events}`,
      });
    });

    return Object.values(buckets)
      .sort((a, b) => String(a.isin).localeCompare(String(b.isin)))
      .map((row) => ({
        ...row,
        paymentsLabel: `${row.events} payment${row.events === 1 ? '' : 's'}`,
        flows: [...row.flows].sort((a, b) => {
          const da = a.paymentDateParsed?.getTime() ?? Infinity;
          const db = b.paymentDateParsed?.getTime() ?? Infinity;
          return da - db;
        }),
      }));
  }, [visibleFlows]);

  const isinColumns = [
    {
      title: 'ISIN', dataIndex: 'isin', key: 'isin', width: 160,
      render: (v) => <Text code style={{ fontSize: 12 }}>{fmt(v)}</Text>,
    },
    { title: 'Holdings', dataIndex: 'issuer', key: 'issuer', ellipsis: true, render: (v) => fmt(v) },
    { title: 'Payments', dataIndex: 'paymentsLabel', key: 'paymentsLabel', width: 120 },
    {
      title: 'Total cash flow (₹)', dataIndex: 'amount', key: 'amount', align: 'right', width: 160,
      render: (v) => <span style={{ fontWeight: 600, color: '#0f766e' }}>{currency(v)}</span>,
    },
  ];

  const detailColumns = [
    {
      title: 'Payment date', dataIndex: 'paymentDate', key: 'paymentDate', width: 130,
      render: (v) => fmt(v),
    },
    {
      title: 'Schedule type', dataIndex: 'scheduleType', key: 'scheduleType', width: 140,
      render: (v) => <Tag color={scheduleTypeColor(v)}>{formatScheduleType(v)}</Tag>,
    },
    {
      title: 'Payment type', dataIndex: 'paymentType', key: 'paymentType', width: 160,
      render: (v) => fmt(v),
    },
    {
      title: 'Description', dataIndex: 'description', key: 'description', ellipsis: true,
      render: (v) => fmt(v),
    },
    {
      title: 'Amount (₹)', dataIndex: 'scaledAmount', key: 'scaledAmount', align: 'right', width: 140,
      render: (v) => <span style={{ fontWeight: 600, color: '#0f766e' }}>{currency(v)}</span>,
    },
  ];

  if (!holdings?.length) {
    return <Empty description="Add bond holdings to track cash flows" />;
  }

  return (
    <div className="mpd-tab-panel">
      <div className="mpd-tab-panel-head">
        <div>
          <Typography.Title level={5} style={{ margin: 0 }}>
            <CalendarOutlined style={{ marginRight: 8, color: '#0369a1' }} />
            Cash flow schedule
          </Typography.Title>
          <Text type="secondary">
            Track coupon and redemption payments across your bond holdings.
          </Text>
        </div>
        <div className="mpd-cashflow-summary">
          <Tag color="blue">Upcoming total: {currency(upcomingTotal)}</Tag>
          <Tag>{holdings.length} holding{holdings.length === 1 ? '' : 's'}</Tag>
        </div>
      </div>

      <Segmented
        options={VIEW_MODES}
        value={viewMode}
        onChange={(mode) => {
          setViewMode(mode);
          setExpandedKeys([]);
        }}
        className="mpd-cashflow-segment"
      />

      {loading ? (
        <div className="mpd-tab-loading">
          <Spin indicator={<LoadingOutlined spin />} />
          <Text type="secondary">Loading cash flows for all holdings…</Text>
        </div>
      ) : (
        <>
          {error && (
            <Text type="warning" style={{ display: 'block', marginBottom: 12, fontSize: 13 }}>
              {error}
            </Text>
          )}
          {tableData.length === 0 ? (
            <Empty description={`No ${VIEW_MODES.find((m) => m.value === viewMode)?.label || viewMode} cash flows for your holdings`} />
          ) : (
            <Table
              size="small"
              rowKey="key"
              columns={isinColumns}
              dataSource={tableData}
              pagination={false}
              className="mpd-cashflow-table"
              expandable={{
                expandedRowKeys: expandedKeys,
                onExpandedRowsChange: setExpandedKeys,
                expandRowByClick: true,
                expandedRowRender: (record) => (
                  <div className="mpd-cashflow-nested">
                    <Table
                      size="small"
                      rowKey="key"
                      columns={detailColumns}
                      dataSource={record.flows}
                      pagination={false}
                      className="mpd-cashflow-detail-table"
                    />
                  </div>
                ),
              }}
            />
          )}
        </>
      )}
    </div>
  );
}
